import { useState, useEffect, useContext, createContext, FC, useMemo, ReactNode } from 'react';
import GlobalContext from './GlobalContext';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { validateForm, EValidationType, validateEmail } from '../util//validation/formValidation';
import API from '../api/api';
import {
    TLoginPost,
    EErrorResponseType,
    TMigrationRequiredError,
    TApiErrorResponseWUsername,
    TFindConsumerPost,
    TApiErrorResponseWUserId,
    TMigrationRequiredExistingAccountError,
} from '../api/types';
import {
    EQueryStringParam,
    ERoutes,
    ELinks,
    EStaffAccountType,
    EIdentityClientId,
    EThirdPartyProvider,
    maxEmailLength,
    EMixpanelEvent,
    mbEmailDomain,
    EMixpanelAppName,
    EValidHandoffScreens,
} from '../util/constants';
import { TMigrateAccountRouterState } from './MigrateAccountContext';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ELDFlags } from '../util/constants';
import { replacePhrase, replaceElement, mapIdentityClientIdToMixpanelSurface } from '../util/helpers';
import mixpanel from 'mixpanel-browser';
import { TMigrateToExistingAccountRouterState } from './MigrateToExistingAccountContext';
import { Link, useTheme } from '@mui/material';

export type TLoginRouterState = {
    prePopulateForm?: { username?: string };
};

export type TLoginContext = {
    setLoginUiState: (state: ELoginUiState) => void;
    username: string;
    setUsername: (username: string) => void;
    usernameIsValid: boolean;
    usernameValidationMessage: string;
    password: string;
    passwordIsValid: boolean;
    setPassword: (password: string) => void;
    passwordValidationMessage: string;
    firstName: string;
    lastName: string;
    userId: string;
    setUserId: (userId: string) => void;
    checkAzureAdRedirect: () => boolean;
    submitLoginForm: () => Promise<void>;
    setConsumerLoginLink: (consumerLoginLink: string) => void;
    loginUiState: ELoginUiState;
    queryParams: string;
    loginErrorTitle: ReactNode;
    loginErrorMessage: ReactNode;
    loginErrorType: EErrorResponseType | null;
    migrationUser: TMigrationUser;
    migrationUserExistingAccount: TMigrationUserExistingAccount;
    identityClientId: string;
    useConsumerIdentity: boolean;
    redirectURL: string;
    findConsumerUser: () => Promise<void>;
    loginProvider: EThirdPartyProvider | null;
    isCreateAccountRequest: boolean;
};

export enum ELoginUiState {
    default,
    queryStringParsed,
    ready,
    loginConsumer,
    thirdPartyLogin,
    deprecateFacebook,
    createAccount,
    error,
    loginSuccess,
    unverified,
    unverifiedStaff,
    migrationRequired,
    migrationRequiredExistingAccount,
    staffMigrationRequired,
    requireAgreement,
    requireMfa,
}

type TMigrationUser = TMigrateAccountRouterState['migrationUser'];

type TMigrationUserExistingAccount = TMigrateToExistingAccountRouterState;

const LoginContext = createContext<TLoginContext | undefined>(undefined);

export const LoginProvider: FC = ({ children }) => {
    const {
        csrfToken,
        getCsrfToken,
        subscriberId,
        setSubscriberId,
        customerAccountId,
        setCustomerAccountId,
        identityClientId,
        setIdentityClientId,
        getCoBranding,
        coBrandingChecked,
        language,
        returnUrlParameter,
        setReturnUrlParameter,
        setFullLoginLink,
        setConsumerLoginLink,
        setStaffLoginLink,
        isStaffLogin,
        setIsStaffLogin,
        adminBaseUrl,
        staffAccountType,
        setStaffAccountType,
        putFullLoginLinkInLocalStorage,
        fullLoginLink,
        updateLaunchDarklyContext,
        setMixpanelSurface,
    } = useContext(GlobalContext);
    const { search: queryParams } = useLocation();
    const { state: routerState = {} }: { state: TLoginRouterState } = useLocation();
    const [loginUiState, setLoginUiState] = useState(ELoginUiState.default);
    const [loginErrorTitle, setLoginErrorTitle] = useState<string>('');
    const [loginErrorMessage, setLoginErrorMessage] = useState<ReactNode>(language.login.error.generic);
    const [loginErrorType, setLoginErrorType] = useState<EErrorResponseType | null>(null);
    const [username, setUsername] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [loginProvider, setLoginProvider] = useState<EThirdPartyProvider | null>(null);
    const [usernameIsValid, setUsernameIsValid] = useState(true);
    const [usernameValidationMessage, setUsernameValidationMessage] = useState(language.validation.email);
    const [password, setPassword] = useState('');
    const [passwordIsValid, setPasswordIsValid] = useState(true);
    const [passwordValidationMessage, setPasswordValidationMessage] = useState('');
    const [userId, setUserId] = useState('');
    const [redirectURL, setRedirectURL] = useState('');
    const [isCreateAccountRequest, setIsCreateAccountRequest] = useState(false);
    const [migrationUser, setMigrationUser] = useState<TMigrationUser | null>(null);
    const [migrationUserExistingAccount, setMigrationUserExistingAccount] =
        useState<TMigrationUserExistingAccount | null>(null);
    const isPrePopulated = useMemo(() => routerState.hasOwnProperty('prePopulateForm'), [routerState]);
    const theme = useTheme();
    const {
        [ELDFlags.staffIdentity]: useStaffIdentity,
        [ELDFlags.consumerIdentity]: useConsumerIdentity,
        [ELDFlags.deprecateFacebook]: isFacebookDeprecated,
    } = useFlags();

    // ---------
    // Setup
    // --------
    const validateOrMapSurface = ({
        surface,
        identityClientId,
    }: {
        surface?: string;
        identityClientId: EIdentityClientId;
    }) => {
        if (surface && Object.values(EMixpanelAppName).includes(surface.toLocaleLowerCase() as EMixpanelAppName)) {
            return surface as EMixpanelAppName;
        } else {
            const mappedSurface = mapIdentityClientIdToMixpanelSurface(identityClientId);
            return mappedSurface;
        }
    };

    const parseQueryString = () => {
        const decodedReturnUrlParam = decodeURIComponent(
            new URLSearchParams(queryParams).get(EQueryStringParam.returnUrl),
        );
        const allDecodedParams = new URLSearchParams(decodedReturnUrlParam.split('?')[1]);
        const staff = allDecodedParams.get(EQueryStringParam.staff);
        const screen = allDecodedParams.get(EQueryStringParam.handoffScreen);
        const subscriberId = allDecodedParams.get(EQueryStringParam.subscriberId);
        const identityClientId = allDecodedParams.get(EQueryStringParam.clientId) as EIdentityClientId;
        const customerAccountId = allDecodedParams.get(EQueryStringParam.customerAccountId);
        const loginHint = allDecodedParams.get(EQueryStringParam.loginHint);
        const surface = allDecodedParams.get(EQueryStringParam.surface);
        const parsedSurface = validateOrMapSurface({ ...(surface && { surface }), identityClientId });
        setReturnUrlParameter(queryParams.replace('?', ''));
        subscriberId ? setSubscriberId(subscriberId) : setSubscriberId('');
        customerAccountId && setCustomerAccountId(customerAccountId);
        staff && staff.toLowerCase() === 'true' ? setIsStaffLogin(true) : setIsStaffLogin(false);
        screen === EValidHandoffScreens.createAccount && setIsCreateAccountRequest(true);
        setIdentityClientId(identityClientId);
        loginHint && setUsername(loginHint);
        parsedSurface && setMixpanelSurface(parsedSurface);
    };

    const createLoginLinks = (): void => {
        const decodedReturnUrlParam = decodeURIComponent(
            new URLSearchParams(queryParams).get(EQueryStringParam.returnUrl),
        );
        const decodedParamParts = decodedReturnUrlParam.split('?');
        const allParams = new URLSearchParams(decodedParamParts[1]);

        // Generate full login link
        allParams.delete(EQueryStringParam.handoffScreen);
        const fullLoginQueryString = allParams.toString();
        setFullLoginLink(`${ELinks.connectAuthorize}?${fullLoginQueryString}`);

        // Generate consumer login link
        allParams.delete(EQueryStringParam.staff);
        const encodedConsumerLoginQueryString = encodeURIComponent(`${decodedParamParts[0]}?${allParams.toString()}`);
        setConsumerLoginLink(`${ERoutes.login}?${EQueryStringParam.returnUrl}=${encodedConsumerLoginQueryString}`);

        // Generate staff login link
        allParams.append(EQueryStringParam.staff, 'true');
        const encodedStaffLoginQueryString = encodeURIComponent(`${decodedParamParts[0]}?${allParams.toString()}`);
        setStaffLoginLink(`${ERoutes.login}?${EQueryStringParam.returnUrl}=${encodedStaffLoginQueryString}`);
    };

    const checkAzureAdRedirect = () => {
        if (
            identityClientId === EIdentityClientId.devPortal ||
            identityClientId === EIdentityClientId.devCommunity ||
            identityClientId === EIdentityClientId.iosExpress ||
            identityClientId === EIdentityClientId.androidExpress
        ) {
            if (username.toLowerCase().includes(mbEmailDomain) && !username.includes('+')) {
                window.location.assign(`${ELinks.loginAzureAd}&${returnUrlParameter}`);
                return true;
            }
        }
        return false;
    };

    const setupData = () => {
        parseQueryString();
        createLoginLinks();
        setLoginUiState(ELoginUiState.queryStringParsed);
    };

    const getInitialData = async () => {
        try {
            await getCsrfToken();
            !coBrandingChecked && (await getCoBranding());
            if (isPrePopulated) {
                const { username } = routerState.prePopulateForm;
                username && setUsername(username);
            }
            // full login link needed for any third party login after redirect to identity
            putFullLoginLinkInLocalStorage();
            setLoginUiState(ELoginUiState.ready);
        } catch {
            setLoginUiState(ELoginUiState.error);
        }
    };

    useEffect(() => {
        switch (loginUiState) {
            case ELoginUiState.default:
                setupData();
                break;
            case ELoginUiState.queryStringParsed:
                getInitialData();
                break;
            default:
                break;
        }
    }, [loginUiState]);

    useEffect(() => {
        if (isStaffLogin) {
            if (customerAccountId && useStaffIdentity) {
                setStaffAccountType(EStaffAccountType.account);
            } else if (customerAccountId && !subscriberId) {
                setStaffAccountType(EStaffAccountType.account);
            } else {
                setStaffAccountType(EStaffAccountType.subscriber);
            }
        }
    }, [isStaffLogin, customerAccountId, subscriberId, useStaffIdentity]);

    //---------------
    // Find Consumer
    // --------------
    const checkConsumerEmailIsValid = () => {
        let isValid = true;
        let validationMessage = '';
        if (!username.length) {
            isValid = false;
            validationMessage = language.validation.emailMissing;
        }
        if (username.length > maxEmailLength) {
            isValid = false;
            validationMessage = language.validation.emailMaxLength;
        }
        setUsernameIsValid(isValid);
        setUsernameValidationMessage(validationMessage);
        return isValid;
    };

    const findConsumerUser = async () => {
        const emailIsValid = checkConsumerEmailIsValid();
        if (emailIsValid) {
            const postObject: TFindConsumerPost = {
                username,
                ...(subscriberId && { subscriberId: parseInt(subscriberId) }),
            };
            try {
                const response = await API.post.findConsumer(postObject, csrfToken);
                const { firstName, lastName, providers } = response.data;
                setFirstName(firstName);
                setLastName(lastName);
                const providerName = providers.length ? providers[0].providerName : null;
                const validProvider = Object.values(EThirdPartyProvider).includes(providerName);
                if (validProvider) {
                    if (isFacebookDeprecated && providerName === EThirdPartyProvider.facebook) {
                        setLoginUiState(ELoginUiState.deprecateFacebook);
                    } else {
                        setLoginProvider(providerName);
                        setLoginUiState(ELoginUiState.thirdPartyLogin);
                    }
                } else {
                    // csrf token used, get a new one
                    mixpanel.track(EMixpanelEvent.actionStart, { key: 'consumer_signin' });
                    await getCsrfToken();
                    setLoginUiState(ELoginUiState.loginConsumer);
                }
            } catch (error) {
                const { status, data } = error.response;
                if (status === 400 && data.type === EErrorResponseType.invalidCsrfToken) {
                    window.location.reload();
                }
                if (status === 404) {
                    mixpanel.track(EMixpanelEvent.actionStart, { key: 'consumer_create_account' });
                    setLoginUiState(ELoginUiState.createAccount);
                } else {
                    setLoginErrorTitle('');
                    setLoginErrorMessage(language.login.error.generic);
                    setLoginUiState(ELoginUiState.error);
                }
            }
        }
    };

    // ---------------
    // Login
    // ---------------
    const validateLoginForm = () => {
        const validatedForm = validateForm({
            username: {
                validationType: EValidationType.required,
                value: username,
            },
            password: {
                value: password,
                validationType: EValidationType.required,
            },
        });
        setUsernameIsValid(validatedForm.validatedItems.username);
        setPasswordIsValid(validatedForm.validatedItems.password);
        return validatedForm;
    };

    const submitLoginForm = async () => {
        const validatedForm = validateLoginForm();
        if (validatedForm.isValid) {
            const postObject: TLoginPost = {
                username,
                password,
                ...(subscriberId && { subscriberId }),
                ...(isStaffLogin ? { isStaff: true } : { isStaff: false }),
                ...(customerAccountId && { customerAccountId }),
            };
            try {
                const queryParams = `?${returnUrlParameter}`;
                const response = await API.post.login(postObject, queryParams, csrfToken);
                const { requireAgreement = false, redirectUrl, requireMfa = false, userId } = response.data;
                mixpanel.identify(userId);
                mixpanel.track(EMixpanelEvent.actionCompleted, { key: 'consumer_signin' });
                setRedirectURL(redirectUrl);
                if (requireMfa) {
                    setLoginUiState(ELoginUiState.requireMfa);
                } else if (requireAgreement) {
                    setLoginUiState(ELoginUiState.requireAgreement);
                } else {
                    setLoginUiState(ELoginUiState.loginSuccess);
                }
            } catch (error) {
                if (error.response && error.response.status) {
                    const { type: errorType = null } = { ...error.response.data };
                    switch (error.response.status) {
                        case 400:
                            if (errorType === EErrorResponseType.expiredPassword) {
                                const linkText = adminBaseUrl.replace('https://', '');
                                const message = (
                                    <>
                                        {`${language.login.error.passwordExpired}`}{' '}
                                        <a href={adminBaseUrl}>{linkText}</a>
                                    </>
                                );
                                setLoginErrorType(EErrorResponseType.expiredPassword);
                                setLoginErrorTitle('');
                                setLoginErrorMessage(message);
                                setLoginUiState(ELoginUiState.error);
                                API.post.trackEvent({ event: 'Sign In || Staff Sign in | Expired Admin creds' });
                            } else if (errorType === EErrorResponseType.migrationRequired) {
                                const { firstName, lastName, username, countryCode, linkingToken } = error.response
                                    .data as TMigrationRequiredError;
                                setMigrationUser({
                                    firstName,
                                    lastName,
                                    countryCode,
                                    linkingToken,
                                    email: validateEmail(username) ? username : '',
                                });
                                if (isStaffLogin) {
                                    setLoginUiState(ELoginUiState.staffMigrationRequired);
                                } else {
                                    setLoginUiState(ELoginUiState.migrationRequired);
                                    mixpanel.track(EMixpanelEvent.actionStart, { key: 'consumer_migration' });
                                }
                            } else if (errorType === EErrorResponseType.migrationRequiredExistingAccount) {
                                const { firstName, lastName, username, userId, custodian, profileId } = error.response
                                    .data as TMigrationRequiredExistingAccountError;
                                const migrationData: TMigrateToExistingAccountRouterState = {
                                    custodian: {
                                        id: userId,
                                        email: username,
                                        firstName: custodian?.firstName || '',
                                        lastName: custodian?.lastName || '',
                                    },
                                    dependent: {
                                        profileId: profileId.toString(),
                                        firstName,
                                        lastName,
                                    },
                                };
                                setMigrationUserExistingAccount(migrationData);
                                setLoginUiState(ELoginUiState.migrationRequiredExistingAccount);
                                mixpanel.track(EMixpanelEvent.actionStart, {
                                    key: 'consumer_migration_existing_account',
                                });
                            } else if (errorType === EErrorResponseType.manualMigrationRequired) {
                                const errorMessage = replaceElement(
                                    language.login.error.manualMigrationRequired,
                                    'link',
                                    <a
                                        style={{ color: 'inherit' }}
                                        href={ELinks.manualMigrationArticle}
                                        target="_blank"
                                    />,
                                );
                                setLoginErrorTitle('');
                                setLoginErrorMessage(errorMessage);
                                setLoginUiState(ELoginUiState.error);
                                API.post.trackEvent({ event: 'Sign In || Migration Error | Non-email username' });
                            } else if (errorType === EErrorResponseType.subscriberConsumerConflict) {
                                const { data }: { data: TApiErrorResponseWUsername } = error.response;
                                const { username: email } = data;
                                let errorMessageWEmail = replacePhrase(
                                    language.login.error.subscriberConsumerConflict,
                                    '<email>',
                                    email,
                                );
                                const errorMessage = replaceElement(
                                    errorMessageWEmail,
                                    'link',
                                    <Link
                                        component={RouterLink}
                                        to={ERoutes.forgotPassword}
                                        color={theme.palette.info.dark}
                                    />,
                                );
                                setLoginErrorType(EErrorResponseType.subscriberConsumerConflict);
                                setLoginErrorTitle('');
                                setLoginErrorMessage(errorMessage);
                                setLoginUiState(ELoginUiState.error);
                                API.post.trackEvent({
                                    event: 'Sign In || Migration Error | Existing MB account not linked',
                                });
                            } else if (errorType === EErrorResponseType.subscriberConsumerExistingLink) {
                                const { data }: { data: TApiErrorResponseWUsername } = error.response;
                                const { username: email } = data;
                                const errorMessageWEmail = replacePhrase(
                                    language.login.error.subscriberConsumerExistingLink,
                                    '<email>',
                                    email,
                                );
                                const errorMessage = replaceElement(
                                    errorMessageWEmail,
                                    'link',
                                    <Link
                                        component={RouterLink}
                                        to={ERoutes.forgotPassword}
                                        color={theme.palette.info.dark}
                                    />,
                                );
                                setLoginErrorType(EErrorResponseType.subscriberConsumerExistingLink);
                                setLoginErrorTitle('');
                                setLoginErrorMessage(errorMessage);
                                setLoginUiState(ELoginUiState.error);
                                API.post.trackEvent({
                                    event: 'Sign In || Migration Error | Profile already linked',
                                });
                            } else if (errorType === EErrorResponseType.adminStaffSignin) {
                                const errorMessage = replaceElement(
                                    language.login.error.adminStaffSignin,
                                    'link',
                                    <a href={`${adminBaseUrl}${ELinks.adminLogin}`} />,
                                );
                                setLoginErrorTitle('');
                                setLoginErrorMessage(errorMessage);
                                setLoginUiState(ELoginUiState.error);
                            } else if (errorType === EErrorResponseType.invalidCsrfToken) {
                                window.location.assign(fullLoginLink);
                            } else {
                                setLoginErrorTitle('');
                                setLoginErrorMessage(language.login.error.loginInvalid);
                                setLoginUiState(ELoginUiState.error);
                            }

                            break;
                        case 401:
                            setLoginErrorTitle('');
                            setLoginErrorMessage(language.login.error.loginInvalid);
                            setLoginUiState(ELoginUiState.error);
                            if (isStaffLogin) {
                                API.post.trackEvent({
                                    event: 'Sign In || Staff Sign in | Invalid email or password',
                                });
                            } else {
                                API.post.trackEvent({
                                    event: 'Sign In || Consumer Sign in | Invalid email or password',
                                });
                                mixpanel.track(EMixpanelEvent.actionFailed, { key: 'consumer_signin' });
                            }
                            break;
                        case 403:
                            if (errorType === EErrorResponseType.accountUnverified) {
                                const { data }: { data: TApiErrorResponseWUserId } = error.response;
                                if (staffAccountType === EStaffAccountType.account) {
                                    setLoginUiState(ELoginUiState.unverifiedStaff);
                                } else {
                                    const { userId } = data;
                                    await updateLaunchDarklyContext(userId);
                                    userId && setUserId(data.userId);
                                    setLoginUiState(ELoginUiState.unverified);
                                }
                            } else {
                                setLoginErrorTitle('');
                                setLoginErrorMessage(language.login.error.noPermissions);
                                setLoginUiState(ELoginUiState.error);
                            }
                            break;
                        case 429:
                            const errorMessage = replaceElement(
                                language.login.error.consumerLoginLockedMessage,
                                'link',
                                <Link
                                    component={RouterLink}
                                    to={ERoutes.forgotPassword}
                                    color={theme.palette.info.dark}
                                />,
                            );
                            setLoginErrorTitle(language.login.error.consumerLoginLocked);
                            setLoginErrorMessage(errorMessage);
                            setLoginUiState(ELoginUiState.error);
                            break;
                        default:
                            setLoginErrorTitle('');
                            setLoginErrorMessage(language.login.error.generic);
                            setLoginUiState(ELoginUiState.error);
                            break;
                    }
                } else {
                    setLoginErrorTitle('');
                    setLoginErrorMessage(language.login.error.generic);
                    setLoginUiState(ELoginUiState.error);
                }
            }
        }
    };

    return (
        <LoginContext.Provider
            value={{
                setLoginUiState,
                username,
                setUsername,
                usernameIsValid,
                usernameValidationMessage,
                password,
                setPassword,
                passwordIsValid,
                passwordValidationMessage,
                loginProvider,
                firstName,
                lastName,
                userId,
                setUserId,
                checkAzureAdRedirect,
                queryParams,
                loginUiState,
                submitLoginForm,
                setConsumerLoginLink,
                migrationUser,
                migrationUserExistingAccount,
                loginErrorTitle,
                loginErrorMessage,
                loginErrorType,
                identityClientId,
                useConsumerIdentity,
                redirectURL,
                findConsumerUser,
                isCreateAccountRequest,
            }}
        >
            {children}
        </LoginContext.Provider>
    );
};

export default LoginContext;
